import { z } from "zod";
// =============================================================================
// CAPABILITY REGISTRY
// =============================================================================
//
// Capabilities are the atomic unit of access control. They are granted directly
// to operators (see the `operator_capability` table); roles are just code-defined
// presets that expand into capabilities.
//
// This registry is the single source of truth for which capabilities exist. The
// server (tRPC `requireCapability`) and the client (`useCan`) both validate
// against it, so a capability that isn't listed here is checkable nowhere.
//
// Naming: `domain:action`. Coarse granularity — one capability per admin area.
// The map value is the human-readable label shown in the admin UI.
export const CAPABILITIES = {
    "harvest-template:edit": "Edit harvest operation templates",
    "test-template:edit": "Edit test templates",
    "test-attribute-code:edit": "Edit the results catalogue and lab names",
    "test-attribute-code:publish": "Add results to the catalogue",
    "formula-template:edit": "Edit formula templates",
    "sensory-template:edit": "Edit brew sensory session templates",
    "harvest-template:correct": "Correct any harvest operation template instances",
    "harvest-template:correct-own": "Correct own harvest operation template instances",
    "test-template:correct": "Correct any test template instances",
    "test-template:correct-own": "Correct own test template instances",
    "brewcontrol-nodes:manage": "Manage BrewControl nodes",
    "brewcontrol-device:override": "Force BrewControl device outputs",
    "brewcontrol-device:calibrate": "Run BrewControl calibration routines",
    "device:configure": "Configure online device settings & profiles",
    "batch-clearance:configure": "Configure batch clearance",
    "fermentation-dashboard:edit": "Edit fermentation dashboards",
    "cleaning-procedures:edit": "Edit cleaning procedures",
    "fermentation-registry:edit": "Edit fermentation parameter types & feed line types",
    "inventory:manage": "Manage inventory settings",
    "operators:manage-capabilities": "Manage operator capabilities",
    "food-taster:erase": "Erase a taster's personal data",
};
/**
 * Superuser capability — passes every `hasCapability` check. Stored as a grant
 * like any other, but never appears in the `CAPABILITIES` registry so it cannot
 * be toggled per-area in the admin UI. Survives future capability additions.
 */
export const WILDCARD = "*";
export const ALL_CAPABILITIES = Object.keys(CAPABILITIES);
/** Validates a grant target against the registry (`*` is allowed). */
export const grantableCapabilitySchema = z.union([
    z.literal(WILDCARD),
    z.enum(ALL_CAPABILITIES),
]);
// =============================================================================
// GROUPING (for the admin UI's grouped checkboxes)
// =============================================================================
export const CAPABILITY_GROUPS = [
    {
        group: "Templates",
        capabilities: [
            "harvest-template:edit",
            "test-template:edit",
            "formula-template:edit",
            "sensory-template:edit",
            "harvest-template:correct",
            "harvest-template:correct-own",
            "test-template:correct",
            "test-template:correct-own",
        ],
    },
    {
        group: "Process settings",
        capabilities: [
            "test-attribute-code:edit",
            "test-attribute-code:publish",
            "fermentation-dashboard:edit",
            "batch-clearance:configure",
            "cleaning-procedures:edit",
            "fermentation-registry:edit",
            "inventory:manage",
        ],
    },
    {
        group: "Equipment",
        capabilities: [
            "brewcontrol-nodes:manage",
            "brewcontrol-device:override",
            "brewcontrol-device:calibrate",
            "device:configure",
        ],
    },
    {
        group: "Administration",
        capabilities: ["operators:manage-capabilities", "food-taster:erase"],
    },
];
// =============================================================================
// ROLE PRESETS (code-only convenience — assigning a preset just grants its caps)
// =============================================================================
export const ROLE_PRESETS = {
    admin: [WILDCARD],
    "config-author": [
        "fermentation-dashboard:edit",
        "harvest-template:edit",
        "test-template:edit",
        "test-attribute-code:edit",
        "formula-template:edit",
        "sensory-template:edit",
        "cleaning-procedures:edit",
        "fermentation-registry:edit",
        "harvest-template:correct",
        "test-template:correct",
    ],
};
// =============================================================================
// CHECK (shared by server middleware and client `useCan`)
// =============================================================================
/**
 * Whether a set of granted capability strings satisfies the required capability.
 * The wildcard `*` satisfies everything.
 */
export function hasCapability(granted, cap) {
    return granted.includes(WILDCARD) || granted.includes(cap);
}
const CORRECTION_CAPS = {
    harvest: {
        any: "harvest-template:correct",
        own: "harvest-template:correct-own",
    },
    test: { any: "test-template:correct", own: "test-template:correct-own" },
};
/**
 * The correction tier a grant set has in a domain:
 * `"any"` (may correct any instance), `"own"` (only owned instances), or `null`
 * (may not correct). The `*` wildcard and the `:correct` cap both yield `"any"`.
 */
export function correctionScope(granted, domain) {
    const caps = CORRECTION_CAPS[domain];
    if (hasCapability(granted, caps.any))
        return "any";
    if (hasCapability(granted, caps.own))
        return "own";
    return null;
}
//# sourceMappingURL=capabilities.js.map